/**
 * ImpersonationBanner — persistent top-of-page banner when an admin is
 * impersonating another user. Reads localStorage.admin_before_impersonate
 * (a JSON blob {token, name, email, role}) that was set by the Login-As
 * button on the Team page. Click "Return" to swap tokens back and reload.
 */
import { useEffect, useState } from "react";
import { UserSwitch, ArrowUUpLeft } from "@phosphor-icons/react";

export default function ImpersonationBanner() {
  const [orig, setOrig] = useState(null);

  useEffect(() => {
    const readOrig = () => {
      try { const raw = localStorage.getItem("admin_before_impersonate"); setOrig(raw ? JSON.parse(raw) : null); }
      catch { setOrig(null); }
    };
    readOrig();
    window.addEventListener("storage", readOrig);
    return () => window.removeEventListener("storage", readOrig);
  }, []);

  const returnToAdmin = () => {
    if (!orig?.token) return;
    localStorage.setItem("access_token", orig.token);
    localStorage.removeItem("admin_before_impersonate");
    // Route back to admin home
    window.location.href = "/admin";
  };

  if (!orig) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black shadow-lg border-b-2 border-amber-700"
         data-testid="impersonation-banner">
      <div className="max-w-6xl mx-auto px-3 py-2 flex items-center gap-2 flex-wrap text-sm font-semibold">
        <UserSwitch weight="fill" size={16}/>
        <span className="truncate">
          <b>Impersonating {orig.name || orig.email}</b>
          {orig.role && <span className="ml-1 text-[10px] font-black uppercase tracking-widest bg-black/10 px-1.5 py-0.5 rounded">{orig.role}</span>}
          <span className="ml-2 text-xs opacity-70">— you're seeing what they see</span>
        </span>
        <button type="button" onClick={returnToAdmin} data-testid="impersonation-return-btn"
          className="ml-auto h-7 px-2.5 bg-black text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
          <ArrowUUpLeft weight="bold" size={11}/> Return to my account
        </button>
      </div>
    </div>
  );
}
